/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { Capacitor } from '@capacitor/core'
import axios from 'axios'

/**
 * Detect if is running as App (native capacitor or web debug mode).
 */
function isAppEnv() {
  try {
    // native (Android/iOS) or "force app" in web by env
    return Capacitor.isNativePlatform() || window._env_?.CAPACITOR_DEBUG
  } catch {
    return window._env_?.CAPACITOR_DEBUG
  }
}

/**
 * Robust heuristics for mobile/tablet vs desktop in browser.
 * Covers modern UA (userAgentData), legacy UA and fallback by pointer.
 */
function isMobileBrowser() {
  try {
    // modern chromium
    if (navigator.userAgentData?.mobile != null) {
      return navigator.userAgentData.mobile
    }

    const ua = navigator.userAgent || ''
    // iPhone, iPad (iPadOS 13+ can pretend macOS), Android, etc.
    const mobileRegex =
      /Android|iPhone|iPod|iPad|Mobile|BlackBerry|IEMobile|Opera Mini|Silk|Kindle|webOS/i

    if (mobileRegex.test(ua)) return true

    // Fallback: touch + coarse pointer usually indicates mobile/tablet
    // (not perfect, but helps in desktop masquerade iPadOS)
    const hasTouch =
      'ontouchstart' in window ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 1)

    const mqCoarse = (() => {
      try {
        return window.matchMedia('(pointer: coarse)').matches
      } catch {
        return false
      }
    })()

    return hasTouch && mqCoarse
  } catch {
    return false
  }
}

export function getUrlToken(paramName = 'token') {
  if (typeof window === 'undefined') return null

  const params = new URLSearchParams(window.location.search)
  return params.get(paramName)
}

/**
 * Header value `platform`.
 * - "app"      → native capacitor or CAPACITOR_DEBUG
 * - "mobile"   → browser mobile/tablet
 * - "desktop"  → browser desktop
 */
function getPlatformHeader() {
  if (isAppEnv()) return 'app'
  return isMobileBrowser() ? 'mobile' : 'desktop'
}

/**
 * Insert/update the header platform in axios instance passed.
 */
function applyPlatformHeader(instance) {
  instance.defaults.headers.common.platform = getPlatformHeader()

  // Guarantees update by request (in case the environment changes in runtine)
  instance.interceptors.request.use(config => {
    config.headers = config.headers ?? {}
    config.headers.platform = getPlatformHeader()
    return config
  })
}

const baseURL =
  window.localStorage.getItem('CapacitorStorage.apiUrl') ||
  window._env_?.ENV_API_URL ||
  ''

let dynamicBaseURL = `${baseURL}/intelup/`

function attachUrlTokenInterceptor(client) {
  client.interceptors.request.use(config => {
    const token = getUrlToken('token')

    if (token) {
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${token}`,
      }
    }

    return config
  })
}

const api = axios.create({
  baseURL: dynamicBaseURL,
  validateStatus(status) {
    return status >= 200 && status < 500 // default
  },
})

// add postOrPut helper
api.postOrPut = (url, id, data, config = {}) => {
  const method = id ? 'put' : 'post'
  const apiUrl = id ? `${url}/${id}` : url
  return api[method](apiUrl, data, config)
}

// insert header platform (defaults + by request)
applyPlatformHeader(api)
api.interceptors.request.use(config => {
  config.baseURL = dynamicBaseURL
  return config
})

api.interceptors.response.use(undefined, error => {
  // log compact and useful
  console.table({
    code: error.response?.status,
    url: `${error.response?.config.baseURL}${error.response?.config.url}`,
    description: error.response?.data?.description,
    ...error.response?.data?.traceback,
    ...error.response?.config?.headers,
  })

  throw error
})

export default api

export function axiosInstance(maxStatusCode = 500) {
  const instance = axios.create({
    baseURL: dynamicBaseURL,
    validateStatus(status) {
      return status >= 200 && status < maxStatusCode // custom
    },
  })

  instance.postOrPut = (url, id, data, config = {}) => {
    const method = id ? 'put' : 'post'
    const apiUrl = id ? `${url}/${id}` : url
    return instance[method](apiUrl, data, config)
  }

  // insert header platform (defaults + by request)
  applyPlatformHeader(instance)

  instance.interceptors.request.use(config => {
    config.baseURL = dynamicBaseURL
    return config
  })

  instance.interceptors.response.use(undefined, error => {
    console.table({
      code: error.response?.status,
      url: `${error.response?.config.baseURL}${error.response?.config.url}`,
      description: error.response?.data?.description,
      ...error.response?.data?.traceback,
      ...error.response?.config?.headers,
    })
    throw error
  })

  // keep the same singleton common headers (include auth, etc.)
  instance.defaults.headers.common = api.defaults.headers.common

  return instance
}

export function setBaseURL(newURL) {
  dynamicBaseURL = newURL
}

export const axiosDashboardComponentClient = (() => {
  const client = axiosInstance(400)
  attachUrlTokenInterceptor(client) // For Iframe component
  return client
})()

export const axiosDefaultClient = axiosInstance(400)
